import { Koi } from '@ksyos/koi';
import type { OrderSuppliesEpisodeCategory } from '@common/interfaces/orderSuppliesEpisodeCategory';
import type { OrderSuppliesSupplyCategory } from '@common/interfaces/orderSuppliesSupplyCategory';
import { supplyCategoriesByEpisodeCategory } from '@common/constants/supplyCategoriesByEpisodeCategory';

/**
 * Represents the form state after successful validation.
 * - Delivery information is only present for respiratory allergy supplies
 * - Amount is only present when the supply category has amount options
 * - Required fields are non-nullable
 */
export type OrderSuppliesFormStateValidated = {
    delivery?: {
        city: string;
        houseNumber: string;
        pharmacy: string;
        street: string;
        zipCode: string;
    };
    order: {
        additionalNotes: string | null;
        amount: string | null;
        selectedProduct: string;
        selectedProducts: string[];
    };
    personal: {
        emailAddress: string;
        nameFamily: string;
        nameGiven: string | null;
        nameInfix: string | null;
    };
    practice: {
        city: string;
        houseNumber: string;
        houseNumberSuffix: string | null;
        organizationName: string;
        street: string;
        zipCode: string;
    };
};

export type OrderSuppliesFormStateValidatedWithEpisodeCategoryAndSupplyCategory = OrderSuppliesFormStateValidated & {
    episodeCategory: OrderSuppliesEpisodeCategory;
    supplyCategory: OrderSuppliesSupplyCategory;
};

const deliveryLocationSchema = Koi.object({
    city: Koi.string().max(255).required(),
    houseNumber: Koi.string().max(255).required(),
    pharmacy: Koi.string().max(255).required(),
    street: Koi.string().max(255).required(),
    zipCode: Koi.string().max(255).required(),
});

export function createCategoryOrderSuppliesValidationSchema<
    TEpisodeCategory extends keyof typeof supplyCategoriesByEpisodeCategory,
    TSupplyCategory extends keyof (typeof supplyCategoriesByEpisodeCategory)[TEpisodeCategory],
>(episodeCategory: TEpisodeCategory, supplyCategory: TSupplyCategory) {
    const settings = supplyCategoriesByEpisodeCategory[episodeCategory][supplyCategory] as {
        amount?: number[] | boolean;
        hasDeliveryLocation?: boolean;
        selectionMode: 'single' | 'multiple';
    };

    const validationSchema = createOrderSuppliesValidationSchema(
        settings.selectionMode,
        Boolean(settings.amount),
        settings.hasDeliveryLocation ?? false
    );

    return validationSchema.schema.concat(
        Koi.object({
            episodeCategory: Koi.string().valid(episodeCategory).required(),
            supplyCategory: Koi.custom(validateSupplyCategory).required(),
        })
    );
}
const validateSupplyCategory = (value: unknown, helpers: { state: { ancestors?: { episodeCategory: string }[] } }) => {
    const { ancestors } = helpers.state;

    if (!ancestors) {
        throw new Error('Ancestors are required for supply category validation');
    }

    const { episodeCategory } = ancestors[0];
    const episodeCategoryValue =
        supplyCategoriesByEpisodeCategory[episodeCategory as keyof typeof supplyCategoriesByEpisodeCategory];

    if (!episodeCategoryValue) {
        throw new Error('Invalid episode category');
    }

    const supplyCategory = episodeCategoryValue[value as keyof typeof episodeCategoryValue];

    if (!supplyCategory) {
        throw new Error('Invalid supply category');
    }

    return value;
};

export const createOrderSuppliesValidationSchema = (
    selectionMode: 'single' | 'multiple',
    hasAmount: boolean,
    hasDeliveryLocation: boolean
) => ({
    schema: Koi.object({
        ...(hasDeliveryLocation ? { delivery: deliveryLocationSchema } : {}),
        order: Koi.object({
            ...(hasAmount ? { amount: Koi.string().required() } : {}),
            additionalNotes: Koi.string().max(10000).allow(null, ''),
            ...(selectionMode === 'single' ? { selectedProduct: Koi.string().required() } : {}),
            ...(selectionMode === 'multiple'
                ? { selectedProducts: Koi.array().items(Koi.string()).min(1).required() }
                : {}),
        }),
        personal: Koi.object({
            emailAddress: Koi.string().email({ tlds: false }).trim().max(255).required(),
            nameFamily: Koi.string().max(255).required(),
            nameGiven: Koi.string().max(255).allow(null, ''),
            nameInfix: Koi.string().max(255).allow(null, ''),
        }),
        practice: Koi.object({
            city: Koi.string().max(255).required(),
            houseNumber: Koi.string().max(255).required(),
            houseNumberSuffix: Koi.string().max(255).allow(null, ''),
            organizationName: Koi.string().max(255).required(),
            street: Koi.string().max(255).required(),
            zipCode: Koi.string().max(255).required(),
        }),
    }).options({ convert: false, presence: 'required', stripUnknown: true }),
});
